import React from "react";

interface NodeResult {
  status: "completed" | "failed" | "running" | "pending" | "paused" | "skipped";
  duration_s?: number;
  error?: string;
  started_at?: string;
  completed_at?: string;
  retries?: number;
}

interface WorkflowTimelineProps {
  nodes: Array<{ id: string; type: string; label: string }>;
  nodeResults: Record<string, NodeResult>;
  currentNodeId?: string | null;
  onNodeClick?: (nodeId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  completed: "bg-green-500",
  failed: "bg-red-500",
  running: "bg-blue-500 animate-pulse",
  pending: "bg-zinc-600",
  paused: "bg-yellow-500",
  skipped: "bg-zinc-500",
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

export function WorkflowTimeline({ nodes, nodeResults, currentNodeId, onNodeClick }: WorkflowTimelineProps) {
  const displayNodes = nodes.filter((n) => n.type !== "start" && n.type !== "end");

  if (displayNodes.length === 0) return null;

  return (
    <div className="flex items-center gap-1 w-full py-2">
      {displayNodes.map((node, i) => {
        const result = nodeResults[node.id];
        const status = node.id === currentNodeId ? "running" : (result?.status || "pending");
        const colorClass = STATUS_COLORS[status] || STATUS_COLORS.pending;
        const duration = result?.duration_s;
        const hasError = result?.error;

        return (
          <React.Fragment key={node.id}>
            <button
              onClick={() => onNodeClick?.(node.id)}
              className={`flex-1 rounded px-2 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-80 ${colorClass}`}
              title={hasError ? `${node.label}: ${result!.error}` : node.label}
            >
              <div className="truncate">{node.label}</div>
              {duration != null && (
                <div className="text-[10px] opacity-75">{formatDuration(duration)}</div>
              )}
              {result?.retries ? (
                <div className="text-[10px] opacity-75">{result.retries} retry</div>
              ) : null}
            </button>
            {i < displayNodes.length - 1 && (
              <div className="text-zinc-500 text-xs shrink-0">→</div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
